<script setup lang="ts">
import type { IconName } from "@verbleif/lib/src/components/Icon/icons";
import type { HeadlessProgressToastProps } from "./SonnerCustom";
import VButton from "@verbleif/lib/src/components/Button/VButton.vue";
import VIcon from "@verbleif/lib/src/components/Icon/VIcon.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import CustomProgressBar from "./SonnerProgressBar.vue";
import "./toaster.css";

const {
  title,
  message,
  value,
  isComplete = false,
  isFailed = false,
  notificationIcon,
  hideClose = false,
  hideViolations = false,
  hideProgressBar = false,
  violations,
  secondaryTitle = "",
  messageRight = "",
  secondaryValue,
  secondaryMessage = "",
  secondaryMessageRight = "",
  showSecondaryProgressBar = false,
  buttons,
} = defineProps<HeadlessProgressToastProps>();

defineEmits(["closeToast"]);

const MAX_VIOLATION_LINES = 5;

const { t } = useI18n();

type ProgressToastState = "running" | "success" | "warning" | "error" | "info";

const ICONS: Record<ProgressToastState, IconName> = {
  running: "loader-circle",
  success: "check",
  warning: "triangle-alert",
  error: "circle-alert",
  info: "info",
};

const violationEntries = computed(() => Object.entries(violations ?? {}));
const hasViolations = computed(() => violationEntries.value.length > 0);
const visibleViolations = computed(() => violationEntries.value.slice(0, MAX_VIOLATION_LINES));
const hiddenViolationsCount = computed(() => Math.max(0, violationEntries.value.length - MAX_VIOLATION_LINES));

const state = computed<ProgressToastState>(() => {
  if (notificationIcon) {
    return "info";
  }
  if (isFailed) {
    return "error";
  }
  if (!isComplete) {
    return "running";
  }
  return hasViolations.value ? "warning" : "success";
});

const isRunning = computed(() => state.value === "running");
const icon = computed(() => notificationIcon ?? ICONS[state.value]);
const showClose = computed(() => !hideClose && !isRunning.value);
const showProgressBar = computed(() => value !== undefined && !hideProgressBar);
// Running fills in the light green of the board; a finished bar takes the colour of its state.
const progressFill = computed(() => state.value === "running" ? "var(--vb-fill)" : "var(--vb-fg)");
const buttonList = computed(() => (buttons ?? []).map((button, index, all) => ({
  ...button,
  size: button.size ?? "small",
  variant: button.variant ?? (index === all.length - 1 ? "primary" as const : undefined),
})));

function formatViolation(messages: string[] | string): string {
  return Array.isArray(messages) ? messages.join(", ") : messages;
}
</script>

<template>
  <div
    class="progress-toast"
    :data-vb-tone="state"
    :role="state === 'warning' || state === 'error' ? 'alert' : 'status'"
    :aria-live="isRunning ? 'off' : undefined"
  >
    <div class="header">
      <span class="vb-toast-tile">
        <VIcon :name="icon" :spin="isRunning" aria-hidden="true" />
      </span>
      <div class="title">
        {{ title }}
      </div>
      <button v-if="showClose" type="button" class="close" :aria-label="t('base.close')" @click="$emit('closeToast')">
        <VIcon name="x" aria-hidden="true" />
      </button>
    </div>

    <div class="body">
      <div class="row">
        <p class="message" :class="{ 'message-error': state === 'error' }">
          {{ message }}
        </p>
        <span v-if="messageRight" class="count">{{ messageRight }}</span>
      </div>
      <CustomProgressBar
        v-if="showProgressBar"
        :mode="value === 0 ? 'indeterminate' : 'determinate'"
        :value="value ?? 0"
        :fill="progressFill"
        :label="title"
      />

      <div v-if="showSecondaryProgressBar" class="secondary">
        <p v-if="secondaryTitle" class="secondary-title">
          {{ secondaryTitle }}
        </p>
        <div class="row">
          <p class="message">
            {{ secondaryMessage }}
          </p>
          <span v-if="secondaryMessageRight" class="count">{{ secondaryMessageRight }}</span>
        </div>
        <CustomProgressBar
          v-if="secondaryValue !== undefined"
          :mode="secondaryValue === 0 ? 'indeterminate' : 'determinate'"
          :value="secondaryValue"
          height="4px"
          fill="var(--vb-fill-secondary)"
          :label="secondaryTitle || title"
        />
      </div>

      <ul v-if="hasViolations && !hideViolations" class="violations">
        <li v-for="[key, messages] in visibleViolations" :key="key">
          <span class="violation-key">{{ key }}:</span> {{ formatViolation(messages) }}
        </li>
        <li v-if="hiddenViolationsCount > 0" class="violations-more">
          {{ t("base.progress_toast_more", { count: hiddenViolationsCount }) }}
        </li>
      </ul>
    </div>

    <div v-if="buttonList.length > 0" class="buttons">
      <VButton v-for="(button, index) in buttonList" :key="index" v-bind="button" @click="button.onClick">
        {{ button.label }}
      </VButton>
    </div>
  </div>
</template>

<style scoped>
.progress-toast {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title {
  flex: 1;
  min-width: 0;
  color: var(--vb-title);
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
}

.close {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  width: 24px;
  height: 24px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 50%;
  color: var(--vb-muted);
  cursor: pointer;
  font-size: 14px;
  transition: background-color 150ms, color 150ms;
}

.close:hover {
  background: var(--vb-hover);
  color: var(--vb-muted-hover);
}

.close:focus-visible {
  box-shadow: 0 0 0 2px var(--vb-focus);
  outline: none;
}

.body {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-right: 2px;
}

.row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.message {
  margin: 0;
  min-width: 0;
  color: var(--vb-text);
  font-size: 12px;
  line-height: 17px;
}

.message-error {
  color: var(--vb-fg);
}

.count {
  flex-shrink: 0;
  color: var(--vb-text);
  font-size: 12px;
  line-height: 17px;
  font-variant-numeric: tabular-nums;
}

.secondary {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.secondary-title {
  margin: 0;
  color: var(--vb-title);
  font-size: 12px;
  font-weight: 500;
  line-height: 17px;
}

.violations {
  margin: 2px 0 0;
  padding: 8px 10px;
  background: var(--vb-surface);
  border-radius: 8px;
  color: var(--vb-text);
  font-size: 12px;
  line-height: 17px;
  list-style: none;
  overflow-wrap: anywhere;
}

.violation-key {
  color: var(--vb-title);
  font-weight: 500;
}

.violations-more {
  color: var(--vb-muted);
}

.buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
}
</style>
